import { useMemo } from 'react';
import merge from 'lodash/merge';

import CssBaseline from '@mui/material/CssBaseline';
import {
  createTheme,
  darken,
  getContrastRatio,
  lighten,
  ThemeOptions,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';

import { useSettingsContext } from 'src/components/settings';

// system
import { palette } from './palette';
import { shadows } from './shadows';
import { typography } from './typography';
// options
import RTL from './options/right-to-left';
import { customShadows } from './custom-shadows';
import { componentsOverrides } from './overrides';
import { createPresets } from './options/presets';
import { createContrast } from './options/contrast';
import { useWhiteLabel } from 'src/context/WhiteLabelContext';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

type BrandPalette = ReturnType<typeof palette>['primary'];

function createBrandPalette(
  main: string,
  fallback: BrandPalette,
): BrandPalette {
  try {
    return {
      ...fallback,
      lighter: lighten(main, 0.8),
      light: lighten(main, 0.2),
      main,
      dark: darken(main, 0.2),
      darker: darken(main, 0.4),
      contrastText:
        getContrastRatio(main, '#FFFFFF') >= 4.5 ? '#FFFFFF' : '#000000',
    };
  } catch {
    return fallback;
  }
}

export default function ThemeProvider({ children }: Props) {
  const settings = useSettingsContext();
  const { portal } = useWhiteLabel();

  const presets = createPresets(settings.themeColorPresets);

  const contrast = createContrast(settings.themeContrast, settings.themeMode);

  const memoizedValue = useMemo(() => {
    const basePalette = palette(settings.themeMode);

    return {
      palette: {
        ...basePalette,
        ...presets.palette,
        ...contrast.palette,
        ...(portal && {
          primary: createBrandPalette(
            portal.theme.primary_color,
            basePalette.primary,
          ),
          secondary: createBrandPalette(
            portal.theme.secondary_color,
            basePalette.secondary,
          ),
          background: {
            ...basePalette.background,
            default: portal.theme.background_color,
          },
        }),
      },
      customShadows: {
        ...customShadows(settings.themeMode),
        ...presets.customShadows,
      },
      direction: settings.themeDirection,
      shadows: shadows(settings.themeMode),
      shape: {
        borderRadius: portal?.theme.button_style === 'square' ? 0 : 8,
      },
      typography,
    };
  }, [
    settings.themeMode,
    settings.themeDirection,
    presets.palette,
    presets.customShadows,
    contrast.palette,
    portal,
  ]);

  const theme = createTheme(memoizedValue as ThemeOptions);

  theme.components = merge(componentsOverrides(theme), contrast.components);

  return (
    <MuiThemeProvider theme={theme}>
      <RTL themeDirection={settings.themeDirection}>
        <CssBaseline />
        {children}
      </RTL>
    </MuiThemeProvider>
  );
}
